import React, { useCallback, useState } from 'react';
import { ShieldCheckIcon, PlayIcon, RotateCcwIcon, AlertCircleIcon, XCircleIcon } from 'lucide-react';
import { ProcessingStatus } from '../types/borrowerChecker';
import { useSnapshotProcessor } from '../hooks/useSnapshotProcessor';
import { SAMPLE_SNAPSHOT_CSV } from '../utils/mockData';
import { FileUpload } from '../components/FileUpload';
import { ProcessingStatusIndicator } from '../components/ProcessingStatus';
import { SummaryCards } from '../components/SummaryCards';
import { MissingRecordsTable } from '../components/MissingRecordsTable';
import { AuditDownloads } from '../components/AuditDownloads';
import { EmailStatus } from '../components/EmailStatus';
export function BorrowerNameChecker() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const {
    status,
    result,
    auditRecord,
    processFile,
    reset
  } = useSnapshotProcessor();
  const handleFileSelect = useCallback((file: File) => {
    setSelectedFile(file);
  }, []);
  const handleFileRemove = useCallback(() => {
    setSelectedFile(null);
  }, []);
  const handleUseSampleData = useCallback(() => {
    const blob = new Blob([SAMPLE_SNAPSHOT_CSV], {
      type: 'text/csv'
    });
    const sampleFile = new File([blob], 'sample_lender_snapshot.csv', {
      type: 'text/csv'
    });
    setSelectedFile(sampleFile);
  }, []);
  const handleProcess = useCallback(async () => {
    if (!selectedFile) return;
    await processFile(selectedFile);
  }, [selectedFile, processFile]);
  const handleReset = useCallback(() => {
    setSelectedFile(null);
    reset();
  }, [reset]);
  const isProcessing = status === ProcessingStatus.Validating || status === ProcessingStatus.Processing;
  const isIdle = status === ProcessingStatus.Idle || status === ProcessingStatus.FileSelected;
  const isComplete = status === ProcessingStatus.Success || status === ProcessingStatus.Failed;
  return <div className="min-h-screen w-full bg-gray-50" data-id="element-136">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-id="element-137">
        {/* Header */}
        <header className="mb-8" data-id="element-138">
          <div className="flex items-center gap-3 mb-2" data-id="element-139">
            <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center" data-id="element-140">
              <ShieldCheckIcon className="w-5 h-5 text-white" aria-hidden="true" data-id="element-141" />
            </div>
            <div data-id="element-142">
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-143">
                Borrower Name Checker
              </h1>
              <p className="text-sm text-gray-500" data-id="element-144">
                Upload a lender snapshot to identify missing internal records
              </p>
            </div>
          </div>
        </header>

        {/* Permission Badge */}
        <div className="mb-6" data-id="element-145">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200" data-id="element-146">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500" aria-hidden="true" data-id="element-147" />
            Authorized · Sarah Chen
          </span>
        </div>

        {/* Main Content */}
        <main className="space-y-6" data-id="element-148">
          {/* Upload Section — visible when idle */}
          {isIdle && <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-6" aria-label="Upload snapshot" data-id="element-149">
              <h2 className="text-base font-semibold text-gray-900 mb-1" data-id="element-150">
                Upload Lender Snapshot
              </h2>
              <p className="text-sm text-gray-500 mb-5" data-id="element-151">
                Upload a CSV or Excel file containing lender loan data. The file
                must include a <strong data-id="element-152">Lender Loan ID</strong> column.
              </p>
              <FileUpload file={selectedFile} onFileSelect={handleFileSelect} onFileRemove={handleFileRemove} data-id="element-153" />
              {!selectedFile && <div className="mt-4 flex items-center gap-3" data-id="element-154">
                  <div className="h-px flex-1 bg-gray-200" data-id="element-155" />
                  <span className="text-xs text-gray-400 font-medium" data-id="element-156">or</span>
                  <div className="h-px flex-1 bg-gray-200" data-id="element-157" />
                </div>}
              {!selectedFile && <button type="button" onClick={handleUseSampleData} className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 hover:bg-blue-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2" data-id="element-158">
                  <PlayIcon className="w-4 h-4" aria-hidden="true" data-id="element-159" />
                  Use Sample Data (23 rows — includes missing, duplicates &amp;
                  errors)
                </button>}
              <div className="mt-5 flex items-center justify-between" data-id="element-160">
                <p className="text-xs text-gray-400" data-id="element-161">
                  Required columns: Lender Loan ID, Borrower Name
                </p>
                <button type="button" onClick={handleProcess} disabled={!selectedFile} className={`
                    inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold
                    transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2
                    ${selectedFile ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}
                  `} aria-label="Process snapshot file" data-id="element-162">
                  <PlayIcon className="w-4 h-4" aria-hidden="true" data-id="element-163" />
                  Process Snapshot
                </button>
              </div>
            </section>}

          {/* Processing State */}
          {isProcessing && <ProcessingStatusIndicator data-id="element-164" />}

          {/* Failed State */}
          {status === ProcessingStatus.Failed && result && <div className="bg-red-50 border border-red-200 rounded-lg p-5" data-id="element-165">
              <div className="flex items-start gap-3" data-id="element-166">
                <XCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-167" />
                <div data-id="element-168">
                  <h3 className="text-sm font-semibold text-red-800" data-id="element-169">
                    Processing Failed
                  </h3>
                  <p className="text-sm text-red-700 mt-1" data-id="element-170">
                    {result.errorMessage}
                  </p>
                  <p className="text-xs text-red-600 mt-2" data-id="element-171">
                    An audit record has been generated for this failed run. You
                    can download it below.
                  </p>
                </div>
              </div>
            </div>}

          {/* Success State */}
          {status === ProcessingStatus.Success && result && <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-172">
              <div className="flex items-center gap-2" data-id="element-173">
                <AlertCircleIcon className="w-5 h-5 text-blue-600 flex-shrink-0" aria-hidden="true" data-id="element-174" />
                <p className="text-sm text-blue-800 font-medium" data-id="element-175">
                  Snapshot processed successfully — {result.missingCount}{' '}
                  missing record{result.missingCount !== 1 ? 's' : ''}{' '}
                  identified from {result.totalRows} total rows.
                </p>
              </div>
            </div>}

          {/* Results — Summary Cards */}
          {isComplete && result && result.totalRows > 0 && <SummaryCards totalRows={result.totalRows} validRows={result.validRows} matchedCount={result.matchedCount} missingCount={result.missingCount} duplicateCount={result.duplicateCount} validationErrorCount={result.validationErrorCount} data-id="element-176" />}

          {/* Results — Missing Records Table */}
          {isComplete && result && <MissingRecordsTable records={result.missingRecords} data-id="element-177" />}

          {/* Results — Audit Downloads */}
          {isComplete && result && auditRecord && <AuditDownloads result={result} auditRecord={auditRecord} data-id="element-178" />}

          {/* Results — Email Status */}
          {status === ProcessingStatus.Success && result && <EmailStatus timestamp={result.runTimestamp} snapshotFilename={result.snapshotFilename} missingCount={result.missingCount} data-id="element-179" />}

          {/* Reset Button */}
          {isComplete && <div className="flex justify-center pt-2" data-id="element-180">
              <button type="button" onClick={handleReset} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2" aria-label="Start a new borrower name check" data-id="element-181">
                <RotateCcwIcon className="w-4 h-4" aria-hidden="true" data-id="element-182" />
                Start New Check
              </button>
            </div>}
        </main>

        {/* Footer */}
        <footer className="mt-12 pt-6 border-t border-gray-200" data-id="element-183">
          <p className="text-xs text-gray-400 text-center" data-id="element-184">
            Borrower Name Checker · Processing runs in system context · Audit
            files linked to anchor record
          </p>
        </footer>
      </div>
    </div>;
}